import {
  Combobox,
  HStack,
  IconButton,
  Portal,
  Tag,
  Text,
  VStack,
  createListCollection,
} from '@chakra-ui/react'
import { X } from 'lucide-react'
import type React from 'react'
import { useMemo, useState } from 'react'

export interface CreatableMultiSelectProps {
  value: string[]
  onChange: (value: string[]) => void
  options: string[]
  placeholder?: string
  isClearable?: boolean
}

export function CreatableMultiSelect({
  value,
  onChange,
  options,
  placeholder = 'Create or select options',
  isClearable = true,
}: Readonly<CreatableMultiSelectProps>) {
  const [inputValue, setInputValue] = useState('')

  // Create collection with existing options plus current input if it's new
  const collection = useMemo(() => {
    const allOptions = [...options]

    // Add current input as an option if it's not empty and not already in options/value
    if (
      inputValue.trim() &&
      !allOptions.includes(inputValue.trim()) &&
      !value.includes(inputValue.trim())
    ) {
      allOptions.unshift(inputValue.trim())
    }

    return createListCollection({
      items: allOptions.map((option) => ({
        label: option,
        value: option,
      })),
    })
  }, [options, inputValue, value])

  const handleSelectionChange = (details: { value: string[] }) => {
    const newValue = details.value[0]
    if (newValue && !value.includes(newValue)) {
      onChange([...value, newValue])
      setInputValue('')
    }
  }

  const handleInputChange = (details: { inputValue: string }) => {
    setInputValue(details.inputValue)
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault()
      const trimmedInput = inputValue.trim()
      if (trimmedInput && !value.includes(trimmedInput)) {
        onChange([...value, trimmedInput])
        setInputValue('')
      }
    } else if (event.key === 'Backspace' && !inputValue && value.length > 0) {
      // Remove last tag when backspacing with empty input
      onChange(value.slice(0, -1))
    }
  }

  const removeTag = (tagToRemove: string) => {
    onChange(value.filter((tag) => tag !== tagToRemove))
  }

  const clearAll = () => {
    onChange([])
    setInputValue('')
  }

  return (
    <VStack align="stretch" gap={2}>
      {/* Selected tags display */}
      {value.length > 0 && (
        <HStack wrap="wrap" gap={1}>
          {value.map((tag) => (
            <Tag.Root key={tag} size="sm" colorPalette="blue">
              <Tag.Label>{tag}</Tag.Label>
              <Tag.CloseTrigger asChild>
                <IconButton
                  variant="ghost"
                  size="2xs"
                  onClick={() => removeTag(tag)}
                >
                  <X />
                </IconButton>
              </Tag.CloseTrigger>
            </Tag.Root>
          ))}
          {isClearable && (
            <IconButton
              variant="ghost"
              size="xs"
              onClick={clearAll}
              aria-label="Clear all tags"
            >
              <X />
            </IconButton>
          )}
        </HStack>
      )}

      {/* Combobox for input and suggestions */}
      <Combobox.Root
        collection={collection}
        onValueChange={handleSelectionChange}
        onInputValueChange={handleInputChange}
        inputValue={inputValue}
        openOnClick
        allowCustomValue
      >
        <Combobox.Control>
          <Combobox.Input placeholder={placeholder} onKeyDown={handleKeyDown} />
          <Combobox.Trigger />
        </Combobox.Control>
        <Portal>
          <Combobox.Positioner>
            <Combobox.Content>
              {collection.items.length === 0 && (
                <Combobox.Empty>No options available</Combobox.Empty>
              )}
              {collection.items.map((item) => (
                <Combobox.Item key={item.value} item={item}>
                  <Combobox.ItemText>
                    {!options.includes(item.value) &&
                    inputValue.trim() === item.value ? (
                      <Text>Create "{item.value}"</Text>
                    ) : (
                      item.label
                    )}
                  </Combobox.ItemText>
                  <Combobox.ItemIndicator />
                </Combobox.Item>
              ))}
            </Combobox.Content>
          </Combobox.Positioner>
        </Portal>
      </Combobox.Root>
    </VStack>
  )
}
